/**
 * turnCacheWrites — pure reconciliation ops over a chat's message rows, plus
 * the one thunk that applies them to the RTK Query `getMessages` cache.
 *
 * These used to live inside `useTurnReconciler` (a React hook). They moved here so the
 * app-level [StreamSettler](../stream/StreamSettler.ts) — which settles a turn even
 * when no `ConversationView` is mounted (the cross-chat concurrent streaming case) —
 * can call them WITHOUT a React context.
 */

import { MESSAGE_TYPE } from 'src/content/ChatV2/enums';
import type { ChatMetaEventPayload } from 'src/content/ChatV2/transport/eventSchema';
import type { ChatError } from 'src/content/ChatV2/types/errors';
import { chatApi } from 'src/newServices/chatApi';

import { STOPPED_STATUS_CODE } from './errors';

const STOPPED_STATUS_DETAILS = 'Cancelled by user';
import type { ChatId } from 'src/content/ChatV2/types/ids';

import type { RawChatMessage } from './messageMappers';

/** POST: patch the query row → real id (+ `complete`; a persisted query is done). */
export function applyQueryPersisted(
  rows: RawChatMessage[],
  queryClientId: string,
  serverId: string
): RawChatMessage[] {
  return rows.map((r) =>
    r.clientId === queryClientId
      ? { ...r, serverId, id: serverId, status: 'complete' }
      : r
  );
}

/** POST: patch the response row's real id (id ONLY — content arrives on stream
 * done). **Patch-only — it does NOT create a row.** A mid-stream shell row
 * would collide with the streaming affix's shared key and flash a blank bubble;
 * when the row doesn't exist yet the id waits in the machine context
 * (`responseServerId`) and `upsertResponseContent` stamps it on at settle. */
export function applyResponseId(
  rows: RawChatMessage[],
  responseClientId: string,
  serverId: string,
  modelId?: number
): RawChatMessage[] {
  const idx = rows.findIndex(
    (r) =>
      r.clientId === responseClientId ||
      (serverId != null &&
        r.type === MESSAGE_TYPE.RESPONSE &&
        String(r.id) === String(serverId))
  );
  if (idx === -1) {
    return rows;
  }
  const next = rows.slice();
  next[idx] = {
    ...next[idx],
    serverId,
    id: serverId,
    ...(modelId !== undefined && { model_id: modelId })
  };
  return next;
}

function turnTimestamp(rows: RawChatMessage[]): string {
  const tail = rows[rows.length - 1];
  const inherited = tail?.c_ts;
  return typeof inherited === 'string' && inherited !== ''
    ? inherited
    : new Date().toISOString();
}

function upsertResponseRow(
  rows: RawChatMessage[],
  responseClientId: string,
  serverId: string | undefined,
  content: Record<string, unknown>
): RawChatMessage[] {
  const idx = rows.findIndex((r) => r.clientId === responseClientId);
  if (idx === -1) {
    return [
      ...rows,
      {
        clientId: responseClientId,
        id: serverId ?? responseClientId,
        type: MESSAGE_TYPE.RESPONSE,
        c_ts: turnTimestamp(rows),
        ...content
      }
    ];
  }
  const next = rows.slice();
  next[idx] = { ...next[idx], ...content };
  return next;
}

export function upsertResponseContent(
  rows: RawChatMessage[],
  responseClientId: string,
  text: string,
  files?: unknown[],
  serverId?: string,
  additionalSteps?: unknown,
  modelId?: number
): RawChatMessage[] {
  const content = {
    message: text,
    status: 'complete' as const,
    ...(modelId !== undefined && { model_id: modelId }),
    ...(Array.isArray(files) && files.length > 0 && { files }),
    ...(Array.isArray(additionalSteps) &&
      additionalSteps.length > 0 && { additional_steps: additionalSteps }),
    ...(serverId != null && { serverId, id: serverId })
  };
  return upsertResponseRow(rows, responseClientId, serverId, content);
}

/**
 * STOPPED BY THE USER: settle the turn with whatever it managed to say.
 */
export function upsertStoppedResponse(
  rows: RawChatMessage[],
  responseClientId: string,
  partialText: string,
  files?: unknown[],
  serverId?: string,
  additionalSteps?: unknown
): RawChatMessage[] {
  const content = {
    message: partialText,
    status: 'complete' as const,
    status_code: STOPPED_STATUS_CODE,
    status_details: STOPPED_STATUS_DETAILS,
    ...(Array.isArray(files) && files.length > 0 && { files }),
    ...(Array.isArray(additionalSteps) &&
      additionalSteps.length > 0 && { additional_steps: additionalSteps }),
    ...(serverId != null && { serverId, id: serverId })
  };
  return upsertResponseRow(rows, responseClientId, serverId, content);
}

/**
 * STREAM ERROR: write the turn's failure onto the RESPONSE row.
 */
export function upsertErrorResponse(
  rows: RawChatMessage[],
  responseClientId: string,
  error: ChatError,
  partialText = '',
  serverId?: string
): RawChatMessage[] {
  const content = {
    message: partialText,
    status: 'error' as const,
    error,
    status_code: error.code,
    ...(error.details != null && { status_details: error.details }),
    ...(serverId != null && { serverId, id: serverId })
  };
  return upsertResponseRow(rows, responseClientId, serverId, content);
}

/** The response's citation SOURCES from the `run.completed` payload. The BE
 * reports the list under `files`, `quoted_source`, OR `source` depending on the
 * turn — same priority chain `extractSources` uses for the refresh/history path
 * (see messageMappers.ts), so live-stream and reload agree. */
export function metaSources(
  meta: ChatMetaEventPayload | undefined
): unknown[] | undefined {
  return meta?.files ?? meta?.quoted_source ?? meta?.source;
}

/** Chat-level fields of a chat's cached page, beside `msgs`. */
export interface ChatMetaPatch {
  /** Merged into `chat_data` — writers never drop each other's fields. */
  chatData?: Record<string, unknown>;
  /** Replaces the page's `chat_resources` (GET `chat-msg` row shape). */
  chatResources?: unknown[];
}

/**
 * Write chat-level fields into a chat's cached page — the same places `GET chat-msg`
 * returns them. The ONE writer for every chat-level field: the turn's context-window
 * breakdown (the machine's `meta` is cleared the tick the turn settles, so the cache
 * is the only place a consumer can read it), the composer seed a send implies (model,
 * tools, knowledge mode) and the chat's resources — so a view remounted within the
 * cache lifetime seeds from what the last turn used, not from defaults.
 */
export function patchChatMeta(chatId: ChatId, patch: ChatMetaPatch) {
  return chatApi.util.updateQueryData(
    'getMessages',
    { params: { chat_id: chatId } },
    (draft: {
      data?: { chat_data?: Record<string, unknown>; chat_resources?: unknown };
    }) => {
      if (!draft?.data) {
        return;
      }
      if (patch.chatData) {
        draft.data.chat_data = { ...draft.data.chat_data, ...patch.chatData };
      }
      if (patch.chatResources) {
        draft.data.chat_resources = patch.chatResources;
      }
    }
  );
}

/**
 * Apply a pure op to the SINGLE source of truth — the `getMessages` cache, keyed
 * page-agnostically by `{ params: { chat_id } }` (a new chat's entry is the empty page
 * chatApi returns for its `new-…` key).
 */
export function patchMessagesCache(
  chatId: ChatId,
  mutate: (rows: RawChatMessage[]) => RawChatMessage[]
) {
  return chatApi.util.updateQueryData(
    'getMessages',
    { params: { chat_id: chatId } },
    (draft: { data?: { msgs?: RawChatMessage[] } }) => {
      if (!draft?.data?.msgs) {
        return;
      }
      draft.data.msgs = mutate(draft.data.msgs);
    }
  );
}
